import { AuthErrorCode, SESSION_ERROR_CODES } from "@netmountains/shared/browser";

/**
 * Classification and app-wide broadcasting of API errors that no single component owns.
 *
 * A request rejected by a backend role gate used to reach the SPA as a bare 403 that nothing
 * handled, so the calling page kept rendering without the data it expected and crashed — a white
 * screen instead of a message. Everything here exists so a role rejection surfaces as German text.
 *
 * The API tags every auth failure with an `AuthErrorCode`, so telling "you may not do this" apart
 * from "your session is broken" is an equality check on `code` rather than a guess at the prose.
 */

/** Window event used to tell the app-wide notice that a request was rejected by a role gate. */
export const AUTHORIZATION_ERROR_EVENT = "scopex:authorization-error";

export const AUTHORIZATION_ERROR_MESSAGE =
  "Sie haben keine Berechtigung für diese Aktion. Bitte wenden Sie sich an einen Administrator.";

const GENERIC_ERROR_MESSAGE = "Irgendwas ist schiefgelaufen...";

/**
 * Backend 403 texts that say nothing a user can act on — those get replaced by the German
 * message above. Anything else (e.g. "Substitutes cannot create other substitutes") is a concrete
 * reason and is shown as-is.
 */
const GENERIC_FORBIDDEN_MESSAGES = ["insufficient permissions", "forbidden", "access denied"];

/** A render crash within this window after a 403 is attributed to that 403. */
const AUTHORIZATION_CRASH_WINDOW_MS = 10000;

let lastAuthorizationErrorAt = 0;

/**
 * True when an axios error is a role/permission rejection rather than a session problem.
 *
 * @param {any} error - The rejected value from an axios call.
 * @returns {boolean}
 */
export const isAuthorizationError = error => {
  const response = error?.response;
  if (!response || response.status !== 403) return false;

  const code = response.data?.code;

  // A session failure (CSRF mismatch, token reuse) is the api client's problem, not the user's.
  if (SESSION_ERROR_CODES.includes(code)) return false;

  return true;
};

/** True when the failure means the session is gone and the user has to log in again. */
export const isSessionError = error => SESSION_ERROR_CODES.includes(error?.response?.data?.code);

/*
 * A failed call reaches a component in one of two shapes. The shared `ApiClient` rejects with its
 * own `ApiError`: the status as `statusCode` and the server's answer as `data`, but no `response`
 * (`api/apiClient.js` never installs its interceptors, item 47 of docs/improvement-plan.md). An
 * axios error carries `response.status` and `response.data`. These two read either.
 */

/** The answer the server sent with a failure, or undefined when none came (offline, timeout). */
export const errorBody = error => error?.response?.data ?? error?.data ?? undefined;

/** The HTTP status of a failure, or undefined when no answer came. */
export const errorStatus = error =>
  error?.response?.status ?? (errorBody(error) === undefined ? undefined : error?.statusCode);

/**
 * The server's own reason for refusing a request (a 4xx answer), or undefined. A server fault's
 * text isn't meant for the user, so it is left to the caller's fallback.
 */
export const refusalMessage = error => {
  const status = errorStatus(error);
  return status >= 400 && status < 500 ? errorBody(error)?.message : undefined;
};

/**
 * Reason to show for a role rejection: the backend's own message when it names a concrete rule,
 * otherwise the generic German text.
 *
 * @param {any} error - The rejected value from an axios call.
 * @returns {string}
 */
export const getAuthorizationErrorMessage = error => {
  const message = error?.response?.data?.message;

  if (!message || GENERIC_FORBIDDEN_MESSAGES.includes(message.trim().toLowerCase())) {
    return AUTHORIZATION_ERROR_MESSAGE;
  }
  return message;
};

/**
 * Best user-facing message for a failed request, in German where we control the text.
 *
 * Prefers the server's own message over axios' "Request failed with status code 403".
 *
 * @param {any} error - The rejected value from an axios call.
 * @param {string} [fallback] - Message to use when the server sent none.
 * @returns {string}
 */
export const getApiErrorMessage = (error, fallback) => {
  if (isAuthorizationError(error)) return getAuthorizationErrorMessage(error);

  return error?.response?.data?.message || fallback || error?.message || GENERIC_ERROR_MESSAGE;
};

/**
 * Broadcast a role rejection so the app-wide notice can show it, and remember when it happened
 * so a follow-up render crash can be explained instead of shown as a generic failure.
 *
 * @param {any} error - The rejected value from an axios call.
 */
export const notifyAuthorizationError = error => {
  lastAuthorizationErrorAt = Date.now();

  window.dispatchEvent(
    new CustomEvent(AUTHORIZATION_ERROR_EVENT, {
      detail: {
        message: getAuthorizationErrorMessage(error),
        url: error?.config?.url,
      },
    }),
  );
};

/**
 * Subscribe to role rejections.
 *
 * @param {Function} handler - Called with `{ message, url }`.
 * @returns {Function} Unsubscribe function, ready to return from a `useEffect`.
 */
export const subscribeToAuthorizationErrors = handler => {
  const listener = event => handler(event.detail ?? {});

  window.addEventListener(AUTHORIZATION_ERROR_EVENT, listener);
  return () => window.removeEventListener(AUTHORIZATION_ERROR_EVENT, listener);
};

/** Whether a role rejection happened recently enough to be the cause of a crash. */
export const hadRecentAuthorizationError = () =>
  Date.now() - lastAuthorizationErrorAt < AUTHORIZATION_CRASH_WINDOW_MS;

export { AuthErrorCode };
